/**
 * The graph app: one full-width canvas plus the creation-playback bar.
 * Its settings panel is the app's left sidebar, rendered by AppShell.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { IconButton } from "@/ui/IconButton";
import { useTabs } from "@/store/tabs";
import { useGraphView } from "./useGraphData";
import { GraphCanvas } from "./GraphCanvas";
import type { GraphController } from "./controller";
import s from "./Graph.module.css";

/** Below this the build-up is over before it registers; skip the theater. */
const AUTOPLAY_MIN_NODES = 10;

export function GraphView() {
  const { data, loading } = useGraphView("global");
  const activeTab = useTabs((t) => t.groups.find((g) => g.id === t.activeGroupId)?.activeTabId);
  const groups = useTabs((t) => t.groups);
  const currentItemId =
    groups.flatMap((g) => g.tabs).find((t) => t.id === activeTab)?.itemId ?? null;

  const [controller, setController] = useState<GraphController | null>(null);
  const [, bump] = useState(0);
  const onController = useCallback((c: GraphController | null) => setController(c), []);
  const onPlaybackChange = useCallback(() => bump((n) => n + 1), []);

  // Opening the graph app plays the creation animation: watching the vault
  // assemble itself is the point of the surface. The decision is made ONCE,
  // on the first data delivery after mount, so live rebuilds while the graph
  // is open (a note created, a filter toggled) never restart the show.
  const autoplayDecided = useRef(false);
  useEffect(() => {
    if (autoplayDecided.current || !controller || loading) return;
    // the boot sequence can deliver an EMPTY graph first (the workspace doc
    // loads from IndexedDB after the search mirror), so an empty delivery
    // must not spend the one autoplay decision
    if (data.nodes.length === 0) return;
    autoplayDecided.current = true;
    if (data.nodes.length < AUTOPLAY_MIN_NODES) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    controller.startPlayback();
  }, [controller, loading, data.nodes.length]);

  return (
    <div
      className={s.view}
      data-graph-view
      data-graph-nodes={data.nodes.length}
      data-graph-links={data.links.length}
      data-graph-notes={data.stats.notes}
      data-graph-tags={data.stats.tags}
      data-graph-attachments={data.stats.attachments}
      data-graph-unresolved={data.stats.unresolved}
      data-graph-orphans={data.stats.orphans}
      data-graph-grouped={data.nodes.filter((n) => n.groupColor).length}
    >
      {!loading && data.nodes.length === 0 ? (
        <div className={s.empty}>
          Nothing to graph yet. Link a couple of notes with [[wikilinks]] and they will appear here.
        </div>
      ) : (
        <GraphCanvas
          scope="global"
          data={data}
          currentItemId={currentItemId}
          onController={onController}
          onPlaybackChange={onPlaybackChange}
        >
          <PlaybackBar controller={controller} total={data.nodes.length} />
        </GraphCanvas>
      )}
    </div>
  );
}

/**
 * Obsidian's Animate control. Playback also autoplays once when the app
 * opens (see GraphView), gated on prefers-reduced-motion; this bar is how
 * the user pauses, scrubs, replays or skips it.
 */
function PlaybackBar({ controller, total }: { controller: GraphController | null; total: number }) {
  const [, force] = useState(0);
  const raf = useRef(0);
  const playing = controller?.playback.state === "playing";

  // The playhead lives in the controller, since it advances inside the render
  // loop. Sample it only while actually playing: a permanent rAF loop would
  // re-render this bar 60 times a second for a static graph.
  useEffect(() => {
    if (!controller || !playing) return;
    const tick = () => {
      raf.current = requestAnimationFrame(tick);
      force((n) => (n + 1) % 1_000_000);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [controller, playing]);

  if (!controller || total === 0) return null;
  const pb = controller.playback;
  const topology = controller.topology;
  const shown = pb.isAnimating ? Math.floor(pb.cursor) : total;
  const date = pb.isAnimating && topology ? pb.dateAt(topology) : null;

  return (
    <div className={s.timeline} data-graph-timeline>
      <IconButton
        label={playing ? "Pause" : "Animate note creation"}
        onClick={() => controller.togglePlayback()}
      >
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </IconButton>
      <span className={s.timelineDate}>{date ? formatDate(date) : "All notes"}</span>
      <input
        type="range"
        className={s.scrub}
        min={0}
        max={1}
        step={0.001}
        value={pb.progress}
        onChange={(e) => controller.seekPlayback(Number(e.target.value))}
        aria-label="Creation timeline"
      />
      <span className={s.timelineCount}>
        {shown} / {total}
      </span>
      <IconButton label="Reset" onClick={() => controller.resetPlayback()}>
        <RotateCcw size={14} />
      </IconButton>
    </div>
  );
}

function formatDate(ms: number): string {
  if (!ms) return "";
  return new Date(ms).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}
